import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../features/components/Forms/EditForm'
import BudgetContent from './ContentSections/BudgetContent'
import BidPriceContent from './ContentSections/BidPriceContent'

import { useEditSectionFormSubmit } from '../../../../../hooks/formHooks/useEditSectionFormSubmit'
import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'

import { filterObjectEmptyValues } from '../../../../../helpers/common'

import {
  updateLineItem,
  clearUpdateLineItem,
  updateTikTokLineItemBudget,
  clearUpdateTikTokLineItemBudget
} from '../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector,
  updateTikTokLineItemBudgetErrorSelector,
  updateTikTokLineItemBudgetIsLoadingSelector,
  updateTikTokLineItemBudgetWasUpdatedSelector
} from '../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'

import { formatValuesToFE } from './formatters'
import { getValidationSchema } from './validation'

import { TIKTOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../constants/changeHistory'
import { BID_PRICE } from '../../../../Tiktok/LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/fields'
import { CAMPAIGN_BUDGET } from '../../../../Multiplatform/fields'
import { BUDGET } from '../../../../Tiktok/LineItemForms/LineItemTikTokEdit/fields'
import { BUYING_TYPE } from '../../../../Tiktok/fields'

function TikTokLineItemBudgetSectionForm({
  lineItem,
  providedAdAccountId,
  allowEditBidPrice,
  remainingBudget,
  title,
  ...formProps
}) {
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const { id: selectedTiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const adAccountId = providedAdAccountId || selectedTiktokAdAccountId
  const { [BUDGET]: budget } = lineItem

  // On right summary page we use PATCH request for update TikTok line item budget, and we don't allow edit bid price
  const isPatchBudgetRequest = !allowEditBidPrice

  const isLoadingSelector = isPatchBudgetRequest
    ? updateTikTokLineItemBudgetIsLoadingSelector
    : lineItemUpdateIsLoadingSelector
  const errorSelector = isPatchBudgetRequest ? updateTikTokLineItemBudgetErrorSelector : lineItemUpdateErrorSelector
  const tikTokLineItemBudgetWasUpdated = useSelector(updateTikTokLineItemBudgetWasUpdatedSelector)

  const budgetType = useMemo(() => {
    if (!budget) {
      return CAMPAIGN_BUDGET
    } else {
      return lineItem[BUYING_TYPE]
    }
  }, [budget, lineItem])

  // show bid price field only if it was previously set
  const lineItemBidPrice = !!lineItem?.bid
  const showBidPrice = lineItemBidPrice && allowEditBidPrice

  const handleUpdateLineItemPutRequest = useCallback(
    values => {
      // as we use PUT for update TikTok campaign, we should pass full data in payload
      dispatch(
        updateLineItem(
          {
            ...filterObjectEmptyValues(lineItem),
            account: adAccountId,
            [BUDGET]: values[BUDGET],
            ...(showBidPrice && { bid: values[BID_PRICE] }),
            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          lineItem.id,
          TIKTOK_PLATFORM,
          {
            requestMethod: 'PUT'
          }
        )
      )
    },
    [dispatch, lineItem, showBidPrice, adAccountId, title]
  )

  const handleUpdateLineItemBudgetPatchRequest = useCallback(
    values => {
      dispatch(
        updateTikTokLineItemBudget({
          account: adAccountId,
          [BUDGET]: [
            {
              [BUDGET]: values[BUDGET],
              adgroup_id: lineItem.id
            }
          ],
          meta: {
            [HISTORY_META_EDIT_SECTION]: title
          }
        })
      )
    },
    [dispatch, lineItem, adAccountId, title]
  )

  const handleSubmit = isPatchBudgetRequest ? handleUpdateLineItemBudgetPatchRequest : handleUpdateLineItemPutRequest

  const initialValues = formatValuesToFE(lineItem)

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({
    initialValues,
    // On right summary page we use PATCH request for update TikTok line item budget (we use separate endpoint for this)
    // On campaign summary page we allow to edit bid price, so we need to use PUT request for update line item
    handleSubmit
  })

  const successSubmit = lineItemWasUpdated || tikTokLineItemBudgetWasUpdated || manualSuccessSubmit

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: getValidationSchema({
      remainingBudget,
      lineItem,
      showBidPrice
    }),
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(isPatchBudgetRequest ? clearUpdateTikTokLineItemBudget() : clearUpdateLineItem())
  }, [dispatch, isPatchBudgetRequest])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={successSubmit}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={errorSelector}
      isLoadingSelector={isLoadingSelector}
      {...formProps}
    >
      <BudgetContent formik={formik} budgetType={budgetType} remainingBudget={remainingBudget} />
      {showBidPrice && <BidPriceContent formik={formik} />}
    </EditForm>
  )
}

export default TikTokLineItemBudgetSectionForm
